import type { IStep } from '@chainlit/react-client';

import { normalizeAppLanguage } from './language';

const CHAINLIT_RUN_NAMES = ['on_chat_start', 'on_message', 'on_audio_end'];

/** Recognize both typed runs and legacy Chainlit run names. */
export function isRunStep(step: Pick<IStep, 'type' | 'name'>): boolean {
  return step.type === 'run' || CHAINLIT_RUN_NAMES.includes(step.name);
}

export function isStudentMessageStep(step: Pick<IStep, 'type'>): boolean {
  return step.type.includes('message');
}

/** Flatten internal wrappers while retaining only user-visible nested messages. */
export function studentVisibleDescendants(step: Pick<IStep, 'steps'>): IStep[] {
  return (step.steps || []).flatMap((child) =>
    isStudentMessageStep(child) ? [child] : studentVisibleDescendants(child)
  );
}

export function hasStudentAssistantDescendant(
  step: Pick<IStep, 'steps'>
): boolean {
  return (step.steps || []).some(
    (child) =>
      child.type === 'assistant_message' || hasStudentAssistantDescendant(child)
  );
}

/**
 * Internal run/tool steps are deliberately hidden from students. Keep a
 * visible progress indicator until the first assistant message arrives,
 * regardless of Chainlit's CoT display setting.
 */
export function shouldShowStudentRunLoader(
  step: Pick<IStep, 'steps' | 'end' | 'isError'>,
  loading: boolean
): boolean {
  return Boolean(
    loading &&
      !step.end &&
      !step.isError &&
      !hasStudentAssistantDescendant(step)
  );
}

export type StudentProgressPhase = 'thinking' | 'searching' | 'reading';

function descendants(step: Pick<IStep, 'steps'>): IStep[] {
  return (step.steps || []).flatMap((child) => [child, ...descendants(child)]);
}

function phaseForStep(
  step?: Pick<IStep, 'name' | 'type'>
): StudentProgressPhase {
  if (!step) return 'thinking';
  const label = `${step.type} ${step.name}`.toLocaleLowerCase();
  if (/čitan|citan|read/.test(label)) return 'reading';
  if (
    /pretra|search|lookup|traž|traz|ispit_pretraga|fon_pretraga/.test(label)
  ) {
    return 'searching';
  }
  return 'thinking';
}

/** Return a safe, high-level progress phase without exposing internal payloads. */
export function getStudentProgressPhase(
  step: Pick<IStep, 'steps'>
): StudentProgressPhase {
  const nested = descendants(step);
  const active = nested.filter((child) => !child.end && !child.isError);
  const current = active.at(-1);
  const currentPhase = phaseForStep(current);

  if (currentPhase !== 'thinking') return currentPhase;

  const searched = nested.some((child) => phaseForStep(child) === 'searching');
  if (searched && (!current || current.type === 'llm')) return 'reading';
  return 'thinking';
}

export function getStudentProgressLabel(
  step: Pick<IStep, 'steps'>,
  language?: string | null
): string {
  const phase = getStudentProgressPhase(step);
  const english = normalizeAppLanguage(language) === 'en-US';
  const labels: Record<StudentProgressPhase, [string, string]> = {
    thinking: ['Razmišljam…', 'Thinking…'],
    searching: ['Pretražujem dokumente…', 'Searching documents…'],
    reading: ['Čitam izvore…', 'Reading sources…']
  };
  return labels[phase][english ? 1 : 0];
}
